import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { showToast } from '../../../services';
import { userActions, questionActions, configActions } from '../../../actions';
import { useLoggedUser } from '../../../hooks/useLoggedUser';

const initialState = {
    termsandconditions: '',
    firstbuttontext: 'Accept',
    secondbuttontext: '',
    submitted: false,
    agreed: false
};

const TermsandConditions = () => {
    const [state, setState] = useState(initialState);
    const [flagCoin, setFlagCoin] = useState({});
    const [showCoin, setShowCoin] = useState(false);
    const [coinSpeed, setCoinSpeed] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const dispatch = useDispatch();
    const history = useHistory();
    const { termsandconditions, firstbuttontext, agreed } = state;

    const { appconfigs } = useSelector(state => state.config);
    const coinTimeoutConfig = appconfigs?.find(config => config.ConfigName === 'COINFLAG_TIMEOUT');
    const coinTimeout = coinTimeoutConfig?.ConfigValue || 10000;

    const loggedUser = useLoggedUser();

    useEffect(() => {
        if (!loaded && loggedUser?.data?.LoggedInStatus === "Y") {
            history.push('/');
        }
        setLoaded(true);
    }, [history, loggedUser, loaded]);

    useEffect(() => {
        if (loggedUser) fetchTermsandConditions();
    }, [loggedUser]);

    useEffect(() => {
        dispatch(configActions.fetchAppConfigs());
    }, [dispatch]);

    useEffect(() => {
        dispatch(questionActions.fetchCoinImage())
            .then(response => {
                if (response) setFlagCoin(response);
                if (response && response.CoinSpeed) {
                    switch (response.CoinSpeed) {
                        case 'veryslow':
                            setCoinSpeed(30);
                            break;
                        case 'slow':
                            setCoinSpeed(25);
                            break;
                        case 'medium':
                            setCoinSpeed(20);
                            break;
                        case 'fast':
                            setCoinSpeed(15);
                            break;
                        case 'veryfast':
                            setCoinSpeed(10);
                            break;
                        default:
                            setCoinSpeed(20); // default speed if the value is unrecognized
                    }
                }
            })
            .catch(error => console.error('Error fetching coin image:', error));
    }, [dispatch]);

    const fetchTermsandConditions = () => {
        const popupType = "Terms & Condition";
        dispatch(userActions.fetchTermsandConditions(popupType))
            .then(response => {
                if (response && response.length > 0) {
                    const { MessageText, FirstButtonText, SecondButtonText } = response[0];
                    setState(prevState => ({
                        ...prevState,
                        termsandconditions: MessageText,
                        firstbuttontext: FirstButtonText || 'Accept',
                        secondbuttontext: SecondButtonText
                    }));
                } else {
                    showToast("No terms and conditions found", 'warn');
                }
            })
            .catch(error => {
                showToast(error.toString(), 'error');
                setState(initialState);
            });
    };

    const handleCheckboxChange = () => {
        setState(prevState => ({ ...prevState, agreed: !prevState.agreed }));
    };

    const handleCollectCoin = () => {
        setShowCoin(prevShowCoin => !prevShowCoin);
    }

    const handleSubmit = (event) => {
        event.preventDefault();
        setShowCoin(false);

        const submissionData = {
            StudentProfileID: loggedUser?.data?.StudentProfileID,
            ActionType: "Terms and Conditions Accepted",
            LoggedInStatus: "Y"
        };

        const user = loggedUser ? { ...loggedUser, data: { ...loggedUser.data, LoggedInStatus: "Y" } } : null;
        if (user) {
            localStorage.setItem('user', JSON.stringify(user));
            dispatch(userActions.updateUser(user));
        }

        dispatch(userActions.updateTermsandConditions(submissionData))
            .then(response => {
                if (response && user?.data?.LoggedInStatus === "Y") {
                    history.push('/');
                }
            })
            .catch(error => showToast(error.toString(), 'error'));
    };

    const handleCaptureCoin = () => {
        setShowCoin(false);
        dispatch(userActions.collectCoin({
            StudentProfileID: loggedUser?.data?.StudentProfileID,
            FlagCoins: flagCoin.CoinCount
        }))
            .then(response => {
                if (response && response.ok && loggedUser) {
                    const updatedUser = { ...loggedUser, data: { ...loggedUser.data, T_CCoinCollected: "Y" } };
                    localStorage.setItem('user', JSON.stringify(updatedUser));
                    dispatch(userActions.updateUser(updatedUser));
                }
            })
            .catch(error => {
                showToast(error, 'error');
                setShowCoin(false);
            });
    };

    const coinStyle = {
        animation: `drift ${coinSpeed}s linear infinite`
    };

    return (
        <div className="container-fluid terms-page">
            <div className="content" id="content">
                <div className="d-flex justify-content-center align-items-center flex-column">
                    <h2 className="b-font terms-c main-heading-top">Terms and Conditions</h2>
                    <div className="container term-details-cont w-55">
                        {showCoin && (
                            <div className='animation'>
                                <div className="cloud fcloud01" style={coinStyle}>
                                    <img src={flagCoin.MediaURL} className='w-75' onClick={handleCaptureCoin} alt="Coin" />
                                </div>
                            </div>
                        )}
                        <div className="term-details">
                            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(termsandconditions || '') }}></p>
                            {loggedUser?.data?.T_CCoinCollected === "N" && (
                                <label>
                                    <input
                                        type="checkbox"
                                        onChange={handleCollectCoin}
                                        checked={showCoin}
                                    />
                                    &nbsp; Collect Coin
                                </label>
                            )}
                        </div>
                    </div>
                    <div className="row w-55">
                        <label className='d-flex align-items-center ml-3'>
                            <input
                                type="checkbox"
                                checked={agreed}
                                onChange={handleCheckboxChange}
                                style={{ marginRight: "8px" }}
                            />
                            I agree to the terms and conditions
                        </label>
                    </div>

                    <button
                        className="accept-btn select"
                        onClick={handleSubmit}
                        disabled={!agreed || state.submitted} // Enable only if agreed is true
                    >
                        {firstbuttontext}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TermsandConditions;